import { AuthorDto } from "@growth/dto";
import { Button, ConfirmDialog, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Pencil, Plus, Trash2, UserRound } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ImageField } from "../components/image-field";
import { apiErrorMessage } from "../lib/api-error";
import { useAuthors, useDeleteAuthor, useSaveAuthor } from "../lib/authors";
import { useMe } from "../lib/me";
import { useMembers } from "../lib/workspace";

type Draft = {
  id?: string;
  name: string;
  bio: string;
  avatarUrl: string | null;
  userId: string | null;
};

const emptyDraft = (userId: string | null = null): Draft => ({
  name: "",
  bio: "",
  avatarUrl: null,
  userId,
});

export const AuthorsPage = () => {
  const authors = useAuthors();
  const members = useMembers();
  // Members who have signed in can be linked to a byline.
  const people = (members.data ?? []).flatMap((member) =>
    member.userId ? [{ id: member.userId, email: member.email, name: member.name }] : [],
  );
  const me = useMe(true);
  const [draft, setDraft] = useState<Draft | null>(null);

  const myAuthor = authors.data?.find((author) => author.userId === me.data?.user.id);

  return (
    <section className="max-w-3xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted">Both sites</p>
          <h1 className="font-serif text-3xl text-ink">Authors</h1>
        </div>
        {!draft && (
          <Button
            onClick={() => setDraft(emptyDraft(myAuthor ? null : (me.data?.user.id ?? null)))}
          >
            <Plus className="size-4" />
            New author
          </Button>
        )}
      </header>

      {!myAuthor && me.data && !draft && authors.data && (
        <p className="rounded-lg border border-line bg-white px-4 py-3 text-sm text-muted">
          You don't have a byline yet. Create one linked to your account and your new posts will use
          it by default.
        </p>
      )}

      {draft && (
        <AuthorForm
          draft={draft}
          admins={people}
          authors={authors.data ?? []}
          onDone={() => setDraft(null)}
        />
      )}

      <div className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-white">
        {authors.isPending ? (
          <div className="flex justify-center py-12" aria-label="Loading authors">
            <LoaderCircle className="size-5 animate-spin text-muted" />
          </div>
        ) : authors.isError ? (
          <p role="alert" className="px-6 py-12 text-center text-sm text-red-600">
            {apiErrorMessage(authors.error, "Couldn't load authors")}
          </p>
        ) : authors.data.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-muted">No authors yet.</p>
        ) : (
          authors.data.map((author) => (
            <AuthorRow
              key={author.id}
              author={author}
              linkedEmail={people.find((person) => person.id === author.userId)?.email}
              onEdit={() =>
                setDraft({
                  id: author.id,
                  name: author.name,
                  bio: author.bio ?? "",
                  avatarUrl: author.avatarUrl,
                  userId: author.userId,
                })
              }
            />
          ))
        )}
      </div>
    </section>
  );
};

const AuthorRow = ({
  author,
  linkedEmail,
  onEdit,
}: {
  author: AuthorDto;
  linkedEmail?: string;
  onEdit: () => void;
}) => {
  const deleteAuthor = useDeleteAuthor();

  return (
    <div className="flex items-center gap-4 px-4 py-3">
      {author.avatarUrl ? (
        <img src={author.avatarUrl} alt="" className="size-10 rounded-full object-cover" />
      ) : (
        <span className="flex size-10 items-center justify-center rounded-full bg-paper text-muted">
          <UserRound className="size-5" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">{author.name}</p>
        <p className="truncate text-xs text-muted">
          {author.bio || "No bio"}
          {linkedEmail && <> · linked to {linkedEmail}</>}
        </p>
      </div>
      <Button
        variant="ghost"
        className="h-8 px-2"
        aria-label={`Edit ${author.name}`}
        onClick={onEdit}
      >
        <Pencil className="size-4" />
      </Button>
      <ConfirmDialog
        trigger={
          <Button
            variant="ghost"
            className="h-8 px-2 text-red-600 hover:bg-red-50"
            aria-label={`Delete ${author.name}`}
          >
            <Trash2 className="size-4" />
          </Button>
        }
        title={`Delete ${author.name}?`}
        description="Their posts stay published, without a byline."
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteAuthor.mutate(author.id)}
      />
    </div>
  );
};

const AuthorForm = ({
  draft,
  admins,
  authors,
  onDone,
}: {
  draft: Draft;
  admins: { id: string; email: string; name: string | null }[];
  authors: AuthorDto[];
  onDone: () => void;
}) => {
  const [form, setForm] = useState(draft);
  const save = useSaveAuthor();
  // A person can back only one byline.
  const linkable = admins.filter(
    (admin) => admin.id === draft.userId || !authors.some((a) => a.userId === admin.id),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        id: form.id,
        name: form.name.trim(),
        bio: form.bio.trim() || null,
        avatarUrl: form.avatarUrl,
        userId: form.userId,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <h2 className="font-serif text-lg text-ink">{form.id ? "Edit author" : "New author"}</h2>
      <div className="grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)]">
        <ImageField
          label="Photo"
          aspect="1 / 1"
          value={form.avatarUrl}
          onChange={(avatarUrl) => setForm({ ...form, avatarUrl })}
        />
        <div className="space-y-4">
          <Field label="Name">
            {(props) => (
              <Input
                {...props}
                required
                maxLength={100}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            )}
          </Field>
          <Field label="Short bio" counter={{ value: form.bio.length, max: 500 }}>
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                maxLength={500}
                value={form.bio}
                onChange={(event) => setForm({ ...form, bio: event.target.value })}
              />
            )}
          </Field>
          <Field label="Linked member" hint="Their new posts default to this byline.">
            {(props) => (
              <select
                {...props}
                value={form.userId ?? ""}
                onChange={(event) => setForm({ ...form, userId: event.target.value || null })}
                className="h-9 w-full rounded-md border border-line bg-white px-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              >
                <option value="">Not linked</option>
                {linkable.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name ? `${admin.name} (${admin.email})` : admin.email}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
      </div>
      {save.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiErrorMessage(save.error, "Couldn't save the author")}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending || !form.name.trim()}>
          {save.isPending && <LoaderCircle className="size-4 animate-spin" />}
          {form.id ? "Save" : "Create author"}
        </Button>
      </div>
    </form>
  );
};
